/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * IconRailShell — 48px icon rail on the far left + 200px sidebar beside it.
 * Each module shown as an icon button in the rail. Active module highlighted
 * with its color and a left accent bar.
 */

import { AppVersion } from '@/components/ui/AppVersion';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { useTheme } from '@/lib/theme';
import { useModuleLogos } from '@/stores/layoutStore';
import { projectConfig } from '@/config/project.config';
import type { ModuleShellProps } from './types';

export function IconRailShell({ modules, activeModule, onModuleChange, children }: ModuleShellProps) {
  const { t } = useTranslation('common');
  const { isDark } = useTheme();
  const showLogos = useModuleLogos();
  const logo = isDark ? activeModule.logos.dark : activeModule.logos.light;

  return (
    <div className="ms-rail-container">
      {/* Icon Rail */}
      <div className="ms-rail">
        <div className="ms-rail-items">
          {modules.map((mod) => {
            const isActive = mod.id === activeModule.id;
            const Icon = mod.icon;
            const modLogo = isDark ? mod.logos.dark : mod.logos.light;
            return (
              <button
                key={mod.id}
                className={`ms-rail-btn ${isActive ? 'active' : ''}`}
                onClick={() => { if (!isActive) onModuleChange(mod); }}
                title={t(mod.nameKey)}
                type="button"
                style={isActive ? {
                  '--rail-active-color': mod.color,
                  '--rail-active-bg': `${mod.color}18`,
                } as React.CSSProperties : undefined}
              >
                {isActive && <div className="ms-rail-accent" style={{ background: mod.color }} />}
                {showLogos ? (
                  <img src={modLogo} alt={t(mod.nameKey)} className="ms-rail-btn-logo" />
                ) : (
                  <Icon size={18} />
                )}
                <span className="ms-rail-label">{mod.shortName}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Module Sidebar */}
      <aside className="ms-rail-sidebar">
        {/* Header */}
        <div className="ms-rail-header">
          <div className="ms-rail-brand">
            <div className="ms-rail-logo">
              <img src={logo} alt={t(activeModule.nameKey)} />
            </div>
            <div>
              <div className="ms-rail-title">{t(activeModule.nameKey)}</div>
              <div className="ms-rail-subtitle">{t(activeModule.subtitleKey)}</div>
            </div>
          </div>
        </div>

        {/* Nav content */}
        <div className="ms-rail-nav">{children}</div>

        {/* Footer — legal/copyright only */}
        <div className="ms-rail-footer">
          <div className="ms-rail-legal">
            <span>&copy; 2026 CoreX-Suite</span>
            <span className="ms-rail-legal-sep">&middot;</span>
            <NavLink to="/privacy" className="ms-rail-legal-link">{t('core:sidebar.privacy', 'Privacy')}</NavLink>
            <span className="ms-rail-legal-sep">&middot;</span>
            <NavLink to="/terms" className="ms-rail-legal-link">{t('core:sidebar.terms', 'Terms')}</NavLink>
            <span className="ms-rail-legal-sep">&middot;</span>
            <AppVersion />
          </div>
        </div>
      </aside>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .ms-rail-container {
    --module-shell-width: 248px;
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    display: flex;
    z-index: 100;
  }

  /* Icon Rail */
  .ms-rail {
    width: 48px;
    height: 100%;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 8px 0;
    flex-shrink: 0;
  }

  .ms-rail-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: 0 4px;
  }

  .ms-rail-btn {
    position: relative;
    width: 40px;
    height: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    transition: all 0.15s ease;
    overflow: hidden;
  }

  .ms-rail-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .ms-rail-btn.active {
    background: var(--rail-active-bg, rgba(139, 0, 0, 0.1));
    color: var(--rail-active-color, var(--color-brand));
  }

  .ms-rail-accent {
    position: absolute;
    left: 0;
    top: 6px;
    bottom: 6px;
    width: 3px;
    border-radius: 0 3px 3px 0;
  }

  .ms-rail-btn-logo {
    width: 20px;
    height: 20px;
    object-fit: contain;
    border-radius: 3px;
  }

  .ms-rail-label {
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.02em;
    line-height: 1;
  }

  /* Sidebar */
  .ms-rail-sidebar {
    width: 200px;
    height: 100%;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
  }

  .ms-rail-header {
    padding: 12px 12px;
    border-bottom: 1px solid var(--color-border-subtle);
    flex-shrink: 0;
  }

  .ms-rail-brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ms-rail-logo {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ms-rail-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .ms-rail-title {
    font-family: var(--font-heading);
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
    line-height: 1.2;
  }

  .ms-rail-subtitle {
    font-size: 10px;
    color: var(--color-text-secondary);
  }

  .ms-rail-nav {
    flex: 1;
    overflow-y: auto;
    padding: 4px 6px;
  }

  .ms-rail-footer {
    border-top: 1px solid var(--color-border-subtle);
    padding: 0;
    flex-shrink: 0;
  }

  .ms-rail-legal {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 6px 8px;
    font-size: 0.6rem;
    color: var(--color-text-muted);
  }

  .ms-rail-legal-sep {
    margin: 0 3px;
    color: var(--color-border-subtle);
  }

  .ms-rail-legal-link {
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 0.6rem;
  }

  .ms-rail-legal-link:hover {
    color: var(--color-text-secondary);
  }

  @media (max-width: 768px) {
    .ms-rail-container {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }
  }
`;
